/**
 * Reversible card ↔ detail View-Transition morph.
 *
 * Merges the two halves of the harvested technique:
 *
 *   (a) PER-ENTRY view-transition-name + a SHARED view-transition-class.
 *       Each participating element (a listing card, the detail-page hero) is
 *       given a name UNIQUE to its entry — `card-morph-<id>` — so the browser
 *       pairs the outgoing card with the incoming hero automatically, by entry
 *       identity. VT pseudo-elements cannot wildcard dynamic names (there is no
 *       `::view-transition-group(card-morph-*)`), so every participant also
 *       carries the shared `view-transition-class: card` (declared statically in
 *       resources/css/transitions/_flip.css) — a SINGLE
 *       `::view-transition-group(.card)` rule then styles whichever entry is
 *       morphing.
 *
 *   (b) Name EXACTLY ONE element per side, per navigation. Statically naming
 *       every card would snapshot them all as independent groups (expensive, and
 *       every card would animate). Instead this module names the single card the
 *       reader is navigating to/from — at `visit:start` on the outgoing side and
 *       at `content:replace` on the incoming side — for BOTH directions
 *       (list → detail and detail → list). The names are cleared at `visit:end`
 *       so nothing lingers into the next navigation.
 *
 * Graceful no-op when `document.startViewTransition` is absent: without it Swup
 * runs a plain content swap (its own `native` option collapses to false), so
 * this module assigns no names and the navigation is an ordinary Swup visit.
 *
 * @module transitions/card-morph
 */

/**
 * @typedef {Object} CardMorphConfig
 * @property {string} cardSelector  Listing card element.
 * @property {string} heroSelector  Detail-page morph target (hero).
 * @property {string} idAttribute   Attribute carrying the stable per-entry id,
 *                                   present on BOTH the card and the matching hero.
 * @property {(id: string) => string} name  Builds the per-entry view-transition-name.
 * @property {((url: string) => boolean) | null} isDetailUrl  Optional matcher that
 *                                   classifies a URL as a detail page. When null,
 *                                   direction is inferred from the DOM (a card
 *                                   click ⇒ to-detail; a hero present ⇒ from-detail)
 *                                   and the incoming side is matched by id, no-op
 *                                   if no partner is found.
 */

/** Sensible kit defaults. Projects override selectors + `isDetailUrl`. */
export const DEFAULTS = {
    cardSelector: '[data-card-morph]',
    heroSelector: '[data-card-morph-hero]',
    idAttribute: 'data-card-id',
    name: (id) => `card-morph-${id}`,
    isDetailUrl: null,
};

/**
 * Feature test for the native View Transitions API.
 *
 * @returns {boolean}
 */
function supportsViewTransitions() {
    return typeof document !== 'undefined' && typeof document.startViewTransition === 'function';
}

/**
 * Resolve a Swup visit URL (path + search) against the current origin.
 *
 * @param {(string | undefined)} url
 * @returns {(string | null)}
 */
function resolveUrl(url) {
    if (!url) {
        return null;
    }

    return new URL(url, window.location.origin).href;
}

/**
 * Wire the reversible card ↔ detail morph onto a Swup instance.
 *
 * @param {import('swup').default} swup - The Swup instance.
 * @param {Partial<CardMorphConfig>} [options] - Selector/name/matcher overrides.
 * @returns {void}
 */
export function initCardMorph(swup, options = {}) {
    const config = { ...DEFAULTS, ...options };

    /**
     * Elements whose inline `view-transition-name` this module set, so they can
     * be cleared once the transition finishes.
     * @type {HTMLElement[]}
     */
    let named = [];

    /**
     * The morph in flight: which direction and which entry id, carried from
     * `visit:start` (outgoing side named) to `content:replace` (incoming side named).
     * @type {({ direction: 'to-detail' | 'to-list', id: string }) | null}
     */
    let pending = null;

    /**
     * Assign a per-entry name to one element and remember it for cleanup.
     * @param {HTMLElement} el
     * @param {string} id
     */
    const assignName = (el, id) => {
        el.style.viewTransitionName = config.name(id);
        named.push(el);
    };

    /** Clear every name this module assigned. */
    const clearNames = () => {
        for (const el of named) {
            el.style.viewTransitionName = '';
        }
        named = [];
    };

    /**
     * Find the listing card carrying a given entry id in the current document.
     * @param {string} id
     * @returns {(HTMLElement | null)}
     */
    const findCardById = (id) => {
        const cards = document.querySelectorAll(config.cardSelector);
        for (const card of cards) {
            if (card.getAttribute(config.idAttribute) === id) {
                return /** @type {HTMLElement} */ (card);
            }
        }
        return null;
    };

    /**
     * Whether a URL is (config says) a detail page. With no matcher configured,
     * `whenUnset` is the optimistic assumption used for that direction.
     * @param {(string | undefined)} url
     * @param {boolean} whenUnset
     * @returns {boolean}
     */
    const isDetail = (url, whenUnset) => {
        if (typeof config.isDetailUrl !== 'function') {
            return whenUnset;
        }
        const resolved = resolveUrl(url);
        return resolved ? Boolean(config.isDetailUrl(resolved)) : false;
    };

    // Outgoing side: name exactly one element BEFORE the swap so it lands in the
    // old view-transition snapshot.
    swup.hooks.on('visit:start', (visit) => {
        // Reset defensively — an aborted prior visit could leave state behind.
        clearNames();
        pending = null;

        if (!supportsViewTransitions()) {
            return;
        }

        const cardTrigger = visit.trigger?.el?.closest?.(config.cardSelector) ?? null;
        const hero = document.querySelector(config.heroSelector);

        if (cardTrigger && isDetail(visit.to?.url, /* whenUnset */ true)) {
            // list → detail: morph the clicked card.
            const id = cardTrigger.getAttribute(config.idAttribute);
            if (id) {
                assignName(/** @type {HTMLElement} */ (cardTrigger), id);
                pending = { direction: 'to-detail', id };
            }
        } else if (hero && !isDetail(visit.to?.url, /* whenUnset */ false)) {
            // detail → list: morph the hero back to its listing card.
            const id = hero.getAttribute(config.idAttribute);
            if (id) {
                assignName(/** @type {HTMLElement} */ (hero), id);
                pending = { direction: 'to-list', id };
            }
        }
    });

    // Incoming side: name the partner element AFTER the swap but still inside the
    // view transition (content:replace runs within startViewTransition in native
    // mode), so it is captured in the new snapshot and the pair morphs.
    swup.hooks.on('content:replace', () => {
        if (!pending) {
            return;
        }

        if (pending.direction === 'to-detail') {
            const hero = document.querySelector(config.heroSelector);
            if (hero && hero.getAttribute(config.idAttribute) === pending.id) {
                assignName(/** @type {HTMLElement} */ (hero), pending.id);
            }
        } else {
            const card = findCardById(pending.id);
            if (card) {
                assignName(card, pending.id);
            }
        }
    });

    // Cleanup runs after the transition has finished (visit:end is outside the
    // native VT wrapper) so clearing names never disturbs the running morph.
    swup.hooks.on('visit:end', () => {
        clearNames();
        pending = null;
    });
}
